import React, { useState } from 'react';

type MemoryKind = 'episodic' | 'semantic' | 'procedural';

interface MemoryRecord {
  id: string;
  kind: MemoryKind;
  content: string;
  createdAt: number;
  userId?: string;
  agentId?: string;
  verified: boolean;
}

type NapkinStage = 'idle' | 'spec' | 'build' | 'qa' | 'done';

const MOCK_MEMORIES: MemoryRecord[] = [
  {
    id: 'mem_a1b2c3d4',
    kind: 'semantic',
    content: 'The codebase uses pnpm workspaces with Turbo for build orchestration. Never use npm or yarn.',
    createdAt: Date.now() - 3 * 60 * 60 * 1000,
    agentId: 'builder',
    verified: true,
  },
  {
    id: 'mem_e5f6g7h8',
    kind: 'procedural',
    content: 'To run tests: pnpm test. All three of lint, typecheck, and test must be green before committing.',
    createdAt: Date.now() - 6 * 60 * 60 * 1000,
    agentId: 'tester',
    verified: true,
  },
  {
    id: 'mem_i9j0k1l2',
    kind: 'episodic',
    content: 'Fixed race condition in ws handler (run_3c1e8d) by adding mutex around state mutation.',
    createdAt: Date.now() - 28 * 60 * 1000,
    agentId: 'builder',
    verified: true,
  },
  {
    id: 'mem_m3n4o5p6',
    kind: 'semantic',
    content: 'Ed25519 signer key is loaded from VIBETIME_SIGNER_PRIVATE_KEY env var. Never hardcode keys.',
    createdAt: Date.now() - 2 * 24 * 60 * 60 * 1000,
    agentId: 'system',
    verified: false,
  },
  {
    id: 'mem_q7r8s9t0',
    kind: 'procedural',
    content: 'When adding a new CLI command: import in cli.ts, add USAGE text, add switch case, add package.json dep.',
    createdAt: Date.now() - 4 * 24 * 60 * 60 * 1000,
    agentId: 'builder',
    verified: true,
  },
  {
    id: 'mem_u1v2w3x4',
    kind: 'episodic',
    content: 'Deployment to staging uses github actions, triggered on push to main. Prod is manual via /deploy.',
    createdAt: Date.now() - 7 * 24 * 60 * 60 * 1000,
    verified: true,
  },
];

const KIND_COLORS: Record<MemoryKind, string> = {
  episodic: 'var(--cyan)',
  semantic: 'var(--lavender)',
  procedural: 'var(--gold)',
};

const NAPKIN_STAGES: { stage: NapkinStage; label: string; desc: string }[] = [
  { stage: 'spec', label: 'Spec', desc: 'Converting sketch to structured requirements…' },
  { stage: 'build', label: 'Build', desc: 'Scaffolding app from spec with builder agent…' },
  { stage: 'qa', label: 'Visual QA', desc: 'Running computer-use visual regression checks…' },
  { stage: 'done', label: 'Done', desc: 'App ready — diffs and bundle produced.' },
];

function fmtAgo(ts: number): string {
  const secs = (Date.now() - ts) / 1000;
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

interface MemoryViewProps {
  mode: 'beginner' | 'pro';
}

export function MemoryView({ mode }: MemoryViewProps) {
  const [filterKind, setFilterKind] = useState<MemoryKind | 'all'>('all');
  const [query, setQuery] = useState('');
  const [napkinStage, setNapkinStage] = useState<NapkinStage>('idle');
  const [napkinPrompt, setNapkinPrompt] = useState('');

  const filtered = MOCK_MEMORIES.filter((m) => {
    if (filterKind !== 'all' && m.kind !== filterKind) return false;
    if (query && !m.content.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const startNapkin = () => {
    if (!napkinPrompt.trim()) return;
    setNapkinStage('spec');
    let i = 0;
    const stages: NapkinStage[] = ['spec', 'build', 'qa', 'done'];
    const advance = () => {
      i += 1;
      if (i < stages.length) {
        setNapkinStage(stages[i] as NapkinStage);
        setTimeout(advance, 1800 + Math.random() * 800);
      }
    };
    setTimeout(advance, 1800);
  };

  const counts: Record<MemoryKind, number> = { episodic: 0, semantic: 0, procedural: 0 };
  for (const m of MOCK_MEMORIES) counts[m.kind] += 1;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) 400px',
        gap: 18,
        alignItems: 'start',
      }}
    >
      <div style={{ display: 'grid', gap: 18 }}>
        <section className="panel" style={{ padding: 22 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 16,
            }}
          >
            <div className="section-heading" style={{ marginBottom: 0 }}>
              <span className="section-kicker">Workspace memory</span>
              <strong>Cross-session memory browser</strong>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['all', 'episodic', 'semantic', 'procedural'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: `1px solid ${filterKind === k ? (k === 'all' ? 'rgba(40,215,255,0.4)' : KIND_COLORS[k as MemoryKind] ?? 'var(--line)') : 'var(--line)'}`,
                    background:
                      filterKind === k ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                    color:
                      filterKind === k
                        ? k === 'all'
                          ? 'var(--cyan)'
                          : KIND_COLORS[k as MemoryKind] ?? 'var(--text)'
                        : 'var(--soft)',
                    cursor: 'pointer',
                    fontSize: '0.72rem',
                    fontFamily: 'var(--font-mono)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.07em',
                  }}
                  onClick={() => setFilterKind(k)}
                >
                  {k}
                  {k !== 'all' && (
                    <span style={{ marginLeft: 5, opacity: 0.7 }}>{counts[k as MemoryKind]}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <input
            type="search"
            placeholder="Search memories…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid var(--line)',
              background: 'rgba(0,0,0,0.2)',
              color: 'var(--text)',
              marginBottom: 14,
              outline: 'none',
            }}
          />

          <div style={{ display: 'grid', gap: 10 }}>
            {filtered.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: '0.85rem' }}>
                No memories match your search.
              </div>
            )}
            {filtered.map((m) => (
              <div
                key={m.id}
                style={{
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: `1px solid var(--line)`,
                  background: 'rgba(255,255,255,0.02)',
                  borderLeft: `3px solid ${KIND_COLORS[m.kind]}`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.72rem',
                      color: KIND_COLORS[m.kind],
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {m.kind}
                  </span>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {m.agentId && (
                      <span style={{ fontSize: '0.72rem', color: 'var(--soft)' }}>{m.agentId}</span>
                    )}
                    {m.verified && (
                      <span
                        style={{
                          fontSize: '0.68rem',
                          color: 'var(--mint)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        ✓ signed
                      </span>
                    )}
                    <span style={{ fontSize: '0.72rem', color: 'var(--soft)' }}>
                      {fmtAgo(m.createdAt)}
                    </span>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '0.84rem', lineHeight: 1.55, color: 'var(--text)' }}>
                  {m.content}
                </p>
                {mode === 'pro' && (
                  <div style={{ marginTop: 8 }}>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '0.66rem',
                        color: 'var(--soft)',
                      }}
                    >
                      {m.id}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {mode === 'pro' && (
            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button type="button" className="ghost-button" style={{ fontSize: '0.8rem' }}>
                Add memory
              </button>
              <button type="button" className="ghost-button" style={{ fontSize: '0.8rem' }}>
                Seed from README
              </button>
              <button type="button" className="ghost-button" style={{ fontSize: '0.8rem' }}>
                Mem0 API
              </button>
            </div>
          )}
        </section>
      </div>

      <div style={{ display: 'grid', gap: 18 }}>
        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">Napkin</span>
            <strong>Sketch → spec → app → visual QA</strong>
          </div>

          <div style={{ marginBottom: 14 }}>
            <textarea
              className="composer-input"
              style={{ minHeight: 88 }}
              placeholder="Describe your UI idea or paste a sketch URL…"
              value={napkinPrompt}
              onChange={(e) => setNapkinPrompt(e.target.value)}
              disabled={napkinStage !== 'idle'}
            />
          </div>

          {napkinStage === 'idle' ? (
            <button
              type="button"
              className="primary-button"
              style={{ width: '100%' }}
              onClick={startNapkin}
              disabled={!napkinPrompt.trim()}
            >
              Sketch to app
            </button>
          ) : (
            <div>
              <div
                style={{
                  display: 'flex',
                  gap: 0,
                  marginBottom: 16,
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid var(--line)',
                }}
              >
                {NAPKIN_STAGES.map((s, i) => {
                  const stageIndex = NAPKIN_STAGES.findIndex((x) => x.stage === napkinStage);
                  const isDone = i < stageIndex;
                  const isCurrent = s.stage === napkinStage;
                  return (
                    <div
                      key={s.stage}
                      style={{
                        flex: 1,
                        padding: '10px 8px',
                        textAlign: 'center',
                        fontSize: '0.72rem',
                        fontFamily: 'var(--font-mono)',
                        background: isCurrent
                          ? 'rgba(40,215,255,0.12)'
                          : isDone
                            ? 'rgba(83,246,196,0.08)'
                            : 'rgba(255,255,255,0.02)',
                        color: isCurrent ? 'var(--cyan)' : isDone ? 'var(--mint)' : 'var(--soft)',
                        borderRight: i < NAPKIN_STAGES.length - 1 ? '1px solid var(--line)' : 'none',
                      }}
                    >
                      {isDone ? '✓ ' : isCurrent ? '⟳ ' : ''}
                      {s.label}
                    </div>
                  );
                })}
              </div>

              {napkinStage !== 'done' && (
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: '1px solid var(--line)',
                    fontSize: '0.82rem',
                    color: 'var(--muted)',
                  }}
                >
                  {NAPKIN_STAGES.find((s) => s.stage === napkinStage)?.desc ?? ''}
                </div>
              )}

              {napkinStage === 'done' && (
                <div>
                  <div
                    style={{
                      padding: '14px 16px',
                      borderRadius: 12,
                      border: '1px solid rgba(83,246,196,0.3)',
                      background: 'rgba(83,246,196,0.06)',
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '0.72rem',
                        color: 'var(--mint)',
                        marginBottom: 6,
                      }}
                    >
                      ✓ NAPKIN COMPLETE
                    </div>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text)' }}>
                      {napkinPrompt.slice(0, 60)}… → 3 files, 147 lines, visual QA passed
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="ghost-button"
                      style={{ flex: 1, fontSize: '0.8rem' }}
                    >
                      View diff
                    </button>
                    <button
                      type="button"
                      className="ghost-button"
                      style={{ flex: 1, fontSize: '0.8rem' }}
                      onClick={() => {
                        setNapkinStage('idle');
                        setNapkinPrompt('');
                      }}
                    >
                      New sketch
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">Memory stats</span>
            <strong>Storage overview</strong>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {(Object.keys(counts) as MemoryKind[]).map((k) => (
              <div
                key={k}
                style={{
                  padding: '12px',
                  borderRadius: 12,
                  border: `1px solid ${KIND_COLORS[k]}40`,
                  background: `${KIND_COLORS[k]}08`,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '1.4rem',
                    color: KIND_COLORS[k],
                    marginBottom: 4,
                  }}
                >
                  {counts[k]}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {k}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
